<template>
  <div class="overview-kpis" :class="{ 'overview-kpis--fill': fill }">
    <template v-if="loading">
      <div class="kpi-grid">
        <div v-for="n in 7" :key="n" class="kpi-skeleton"></div>
      </div>
      <div class="kpi-row2" :class="{ 'kpi-row2--single': !show('activity') }">
        <div class="kpi-skeleton kpi-skeleton--chart"></div>
        <div v-if="show('activity')" class="kpi-skeleton kpi-skeleton--chart"></div>
      </div>
    </template>

    <template v-else-if="data">
      <!-- Ligne 1 : tuiles de même structure et de même hauteur -->
      <div v-if="show('tiles')" class="kpi-grid">
        <div v-for="tile in leadingTiles" :key="tile.key" class="kpi-tile">
          <div class="kpi-head">
            <span class="kpi-icon"><i class="material-icons">{{ tile.icon }}</i></span>
            <Tooltip :text="tile.help || ''" :active="!!tile.help" position="top" class="kpi-label-wrap">
              <span class="kpi-label">{{ tile.label }}<i v-if="tile.help" class="material-icons kpi-help">info_outline</i></span>
            </Tooltip>
          </div>
          <Tooltip :text="tile.exact || ''" :active="!!tile.exact" position="top">
            <div class="kpi-value">{{ tile.display }}</div>
          </Tooltip>
          <div class="kpi-foot">
            <template v-if="tile.trend">
              <Tooltip :text="trendHelp" position="top">
                <span class="trend-badge" :class="tile.trend.cls"><i class="material-icons">{{ tile.trend.icon }}</i>{{ tile.trend.text }}</span>
              </Tooltip>
              <Tooltip :text="tile.prevExact" :active="!!tile.prevExact" position="top">
                <span class="kpi-prev">vs {{ tile.prevDisplay }}</span>
              </Tooltip>
            </template>
            <Tooltip v-for="badge in (tile.badges || [])" :key="badge.key" :text="badge.help" position="top">
              <span class="trend-badge" :class="badge.cls">{{ badge.text }}</span>
            </Tooltip>
          </div>
        </div>

        <template v-if="reportingAllowed">
          <div v-for="tile in audienceTiles" :key="tile.key" class="kpi-tile">
            <div class="kpi-head">
              <span class="kpi-icon"><i class="material-icons">{{ tile.icon }}</i></span>
              <Tooltip :text="tile.help" position="top" class="kpi-label-wrap">
                <span class="kpi-label">{{ tile.label }}<i class="material-icons kpi-help">info_outline</i></span>
              </Tooltip>
            </div>
            <Tooltip :text="tile.exact" :active="!!tile.exact" position="top">
              <div class="kpi-value">{{ tile.display }}</div>
            </Tooltip>
            <div class="kpi-foot">
              <template v-if="tile.trend">
                <Tooltip :text="trendHelp" position="top">
                  <span class="trend-badge" :class="tile.trend.cls"><i class="material-icons">{{ tile.trend.icon }}</i>{{ tile.trend.text }}</span>
                </Tooltip>
                <Tooltip :text="tile.prevExact" :active="!!tile.prevExact" position="top">
                  <span class="kpi-prev">vs {{ tile.prevDisplay }}</span>
                </Tooltip>
              </template>
              <Tooltip v-else-if="tile.unavailable" :text="$t('dash.reporting.tooltip.followers_history_unavailable')" position="top">
                <span class="trend-badge flat">—</span>
              </Tooltip>
            </div>
          </div>
        </template>

        <div v-else-if="!tileKeys || audienceKeys.some(k => tileKeys.includes(k))" class="kpi-tile kpi-tile--locked">
          <span class="kpi-icon"><i class="material-icons">lock</i></span>
          <p>{{ $t('dash.overview.kpi.locked') }}</p>
          <button class="ov-btn ov-btn--primary" @click="$emit('go', 'plan')">{{ $t('dash.overview.kpi.upgrade') }}</button>
        </div>
      </div>

      <p v-if="show('tiles')" class="kpi-note">
        <i class="material-icons">info_outline</i>
        <span>
          {{ $t('dash.overview.kpi.scope') }}
          <template v-if="isDayGranularity"> {{ $t('dash.overview.kpi.day_granularity') }}</template>
          <template v-if="pagesWithoutData"> {{ $t('dash.overview.kpi.collecting', { count: pagesWithoutData }) }}</template>
        </span>
      </p>

      <!-- Ligne 2 : évolution + activité de publication (détail réseaux et échecs) -->
      <div v-if="show('chart') || show('activity')" class="kpi-row2" :class="{ 'kpi-row2--single': !(showChart && show('chart')) || !show('activity') }">
        <div class="ov-card kpi-chart" v-if="showChart && show('chart')">
          <div class="ov-block-title">
            <i class="material-icons ov-title-icon">timeline</i>
            <Tooltip :text="$t('dash.overview.help.chart')" position="top">
              <h2 class="ov-help-title">{{ $t('dash.overview.chart.title') }}<i class="material-icons kpi-help">info_outline</i></h2>
            </Tooltip>
          </div>
          <div class="kpi-chart-body">
            <Graph :id="`overview-chart-${chartKey}`" :key="chartKey" :data="chart.data" :labels="chart.labels" :height="fill ? '100%' : '240px'" width="100%" />
          </div>
        </div>

        <div v-if="show('activity')" class="ov-card kpi-activity">
          <div class="ov-block-title">
            <i class="material-icons ov-title-icon">send</i>
            <h2>{{ $t('dash.overview.activity.title') }}</h2>
          </div>

          <Tooltip :text="$t('dash.overview.help.by_network')" position="top">
            <p class="activity-subtitle">{{ $t('dash.overview.activity.by_network') }}<i class="material-icons kpi-help">info_outline</i></p>
          </Tooltip>
          <p v-if="!networks.length" class="activity-empty">{{ $t('dash.overview.activity.no_posts') }}</p>
          <ul v-else class="activity-list">
            <li v-for="n in networks" :key="n.network">
              <span class="activity-label">{{ n.label }}</span>
              <span class="activity-bar"><span :style="{ width: `${n.share}%` }"></span></span>
              <strong>{{ n.count }}</strong>
            </li>
          </ul>

          <Tooltip :text="$t('dash.overview.help.failures')" position="top">
            <p class="activity-subtitle">{{ $t('dash.overview.activity.failures') }}<i class="material-icons kpi-help">info_outline</i></p>
          </Tooltip>
          <p v-if="!failures.total" class="activity-empty">{{ $t('dash.overview.activity.no_failures') }}</p>
          <template v-else>
            <div class="failure-bar">
              <Tooltip v-for="f in failures.items" :key="f.key" :text="`${f.label} : ${f.count} (${f.share} %) — ${$t(`dash.overview.help.failure.${f.key}`)}`" position="top" :style="{ width: `${f.share}%` }" class="failure-segment-wrap">
                <span class="failure-segment" :class="`failure-segment--${f.key}`"></span>
              </Tooltip>
            </div>
            <ul class="activity-list activity-list--failures">
              <li v-for="f in failures.items" :key="f.key">
                <span class="failure-dot" :class="`failure-segment--${f.key}`"></span>
                <Tooltip :text="$t(`dash.overview.help.failure.${f.key}`)" position="top" class="activity-label">
                  <span>{{ f.label }}</span>
                </Tooltip>
                <strong>{{ f.count }} <small>({{ f.share }} %)</small></strong>
              </li>
            </ul>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import Graph from '../reporting-web/Graph.vue'
import Tooltip from '../tooltip/tooltipInit.vue'
import { shortLinksVisible } from '@/utils/shortLinkMetrics'

export default {
  name: 'OverviewKpis',

  components: { Graph, Tooltip },

  props: {
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    reportingAllowed: { type: Boolean, default: true },
    isDayGranularity: { type: Boolean, default: false },
    // Parties affichées (tableaux de bord personnalisés) : tiles, chart, activity
    parts: { type: Array, default: () => ['tiles', 'chart', 'activity'] },
    // Tuiles retenues (null = toutes) : posted, success, fans, impressions, reach, engagement, rate
    tileKeys: { type: Array, default: null },
    // Tableau de bord personnalisé : le bloc remplit la case de la grille (graphique à la hauteur disponible)
    fill: { type: Boolean, default: false },
    // Clics des liens courts mws.li (GET /reporting/short-links) : tuile affichée si raccourcisseur actif et données
    shortLinks: { type: Object, default: null }
  },

  data() {
    return { audienceKeys: ['fans', 'impressions', 'reach', 'engagement', 'rate'] }
  },

  computed: {
    previous() {
      return (this.data && this.data.previous) || null
    },

    /**
     * Pied de la tuile Taux de réussite : échecs comptés dans le taux (erreurs + planifiées non parties),
     * et à part les publications non validées avant leur date, exclues du taux
     */
    successBadges() {
      const pub = this.publishing
      const counted = (pub.failed || 0) + (pub.not_published || 0)
      const badges = []
      if (counted) {
        const detail = [
          pub.failed ? `${pub.failed} ${this.$t('dash.overview.failures.failed')}` : null,
          pub.not_published ? `${pub.not_published} ${this.$t('dash.overview.failures.not_published')}` : null
        ].filter(Boolean).join(', ')
        badges.push({ key: 'counted', cls: 'down', text: this.$t('dash.overview.kpi.failures_count', { count: counted }), help: this.$t('dash.overview.help.success_failures', { detail }) })
      }
      if (pub.not_validated) {
        badges.push({ key: 'not_validated', cls: 'flat', text: this.$t('dash.overview.kpi.not_validated_count', { count: pub.not_validated }), help: this.$t('dash.overview.help.success_not_validated', { count: pub.not_validated }) })
      }
      return badges
    },

    // Période de comparaison affichée dans le Tooltip des tendances
    trendHelp() {
      const prev = this.data && this.data.period && this.data.period.previous
      if (!prev) return ''
      return this.$t('dash.overview.help.trend', { start: moment.unix(prev.since).format('DD/MM/YYYY'), end: moment.unix(prev.until).format('DD/MM/YYYY') })
    },

    publishing() {
      return (this.data && this.data.publishing) || {}
    },

    // Publications (tous réseaux) + taux de réussite
    leadingTiles() {
      return this.publishingTiles.concat(this.shortLinkTiles)
    },
    shortLinkTiles() {
      const sl = this.shortLinks
      if (!shortLinksVisible(sl) || !this.keepTile('short_links')) return []
      return [{
        key: 'short_links', icon: 'touch_app', label: this.$t('dash.overview.kpi.short_links'), help: this.$t('dash.overview.kpi.short_links_tooltip'),
        display: this.format(sl.clicks || 0), prevDisplay: this.format(sl.previous_clicks), trend: this.trend(sl.clicks || 0, sl.previous_clicks),
        exact: this.exactIfCompact(sl.clicks || 0), prevExact: this.exactIfCompact(sl.previous_clicks)
      }]
    },
    publishingTiles() {
      const pub = this.publishing
      const posted = {
        key: 'posted', icon: 'send', label: this.$t('dash.overview.kpi.posted'), help: this.$t('dash.overview.kpi.posted_tooltip'),
        display: this.format(pub.posted), prevDisplay: this.format(pub.previous_posted), trend: this.trend(pub.posted, pub.previous_posted),
        exact: this.exactIfCompact(pub.posted), prevExact: this.exactIfCompact(pub.previous_posted)
      }
      const rate = this.successRate
      const success = {
        key: 'success', icon: 'done_all', label: this.$t('dash.overview.kpi.success_rate_label'), help: this.$t('dash.overview.help.success_rate'),
        display: rate === null ? '—' : `${rate}%`,
        badges: this.successBadges
      }
      return [posted, success].filter(tile => this.keepTile(tile.key))
    },

    audienceTiles() {
      const t = this.data.totals
      const p = this.previous
      const tiles = [
        { key: 'fans', icon: 'group', label: this.$t('dash.reporting.followers'), value: t.page_fans, prev: p ? p.page_fans : null, unavailable: !!p && p.page_fans === null },
        { key: 'impressions', icon: 'visibility', label: this.$t('dash.reporting.impression'), value: t.impressions, prev: p ? p.impressions : null },
        { key: 'reach', icon: 'people', label: this.$t('dash.label.fb.impressions_reach'), value: t.reach, prev: p ? p.reach : null, hidden: !(t.reach > 0) },
        { key: 'engagement', icon: 'favorite', label: this.$t('dash.overview.kpi.engagement'), value: t.engagement, prev: p ? p.engagement : null },
        { key: 'rate', icon: 'trending_up', label: this.$t('dash.overview.kpi.engagement_rate'), value: t.engagement_rate, prev: p ? p.engagement_rate : null, percent: true }
      ]
      return tiles.filter(tile => !tile.hidden && this.keepTile(tile.key)).map(tile => ({
        ...tile,
        help: this.$t(`dash.overview.help.${tile.key}`),
        display: this.format(tile.value, tile.percent),
        prevDisplay: this.format(tile.prev, tile.percent),
        exact: tile.percent ? '' : this.exactIfCompact(tile.value),
        prevExact: tile.percent ? '' : this.exactIfCompact(tile.prev),
        trend: this.trend(tile.value, tile.prev)
      }))
    },

    networks() {
      const byNetwork = this.publishing.by_network || {}
      const entries = Object.entries(byNetwork).filter(([, count]) => count > 0).sort((a, b) => b[1] - a[1])
      const max = entries.length ? entries[0][1] : 1
      return entries.map(([network, count]) => ({ network, count, share: Math.round(count / max * 100), label: this.$t(`dash.overview.network.${network}`) }))
    },

    // Échecs de la période ventilés par cause (mêmes règles que Posts2), avec leur part
    failures() {
      const pub = this.publishing
      const items = [['failed', pub.failed], ['not_published', pub.not_published], ['not_validated', pub.not_validated]]
        .filter(([, n]) => n)
        .map(([key, count]) => ({ key, count, label: this.$t(`dash.overview.failures.${key}`) }))
      const total = items.reduce((s, f) => s + f.count, 0)
      return { total, items: items.map(f => ({ ...f, share: Math.round(f.count / total * 100) })) }
    },

    successRate() {
      const rate = this.publishing.success_rate
      return rate === null || rate === undefined ? null : Math.round(rate * 100)
    },

    pagesWithoutData() {
      return ((this.data && this.data.per_page) || []).filter(p => p.reporting && !p.has_data).length
    },

    chartKey() {
      const period = this.data && this.data.period
      return period ? `${period.since}-${period.until}` : 'none'
    },

    showChart() {
      return this.reportingAllowed && this.chart.data.length > 0
    },

    // Séries regroupées par jour calendaire : les dates ES et celles des posts n'ont pas la même heure
    chart() {
      const series = (this.data && this.data.series) || {}
      const byDay = list => (list || []).reduce((acc, d) => {
        const day = moment(d.date * 1000).format('YYYY-MM-DD')
        acc[day] = (acc[day] || 0) + (d.total || 0)
        return acc
      }, {})
      const impressions = byDay(series.impressions)
      const engagement = byDay(series.engagement)
      const days = [...new Set([...Object.keys(impressions), ...Object.keys(engagement)])].sort()
      if (days.length < 2) return { labels: [], data: [] }
      const data = []
      if (Object.values(impressions).some(v => v > 0))
        data.push({ label: this.$t('dash.reporting.impression'), data: days.map(d => impressions[d] || 0), type: 'bar', color: '#6B1F4D' })
      if (Object.values(engagement).some(v => v > 0))
        data.push({ label: this.$t('dash.overview.kpi.engagement'), data: days.map(d => engagement[d] || 0), type: 'line', color: '#B64985' })
      return { labels: days.map(d => moment(d).format('DD/MM')), data }
    }
  },

  methods: {
    show(part) {
      return this.parts.includes(part)
    },

    keepTile(key) {
      return !this.tileKeys || this.tileKeys.includes(key)
    },

    // Valeur exacte quand l'affichage est abrégé (k, M)
    exactIfCompact(value) {
      return value !== null && value !== undefined && Math.abs(value) >= 10000 ? Number(value).toLocaleString() : ''
    },

    format(value, percent = false) {
      if (value === null || value === undefined) return '—'
      if (percent) return `${Number(value).toFixed(1)}%`
      const abs = Math.abs(value)
      if (abs >= 1000000) return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (abs >= 10000) return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
      return Number(value).toLocaleString()
    },

    trend(value, prev) {
      if (prev === null || prev === undefined || value === null || value === undefined || !prev) return null
      const diff = ((value - prev) / prev) * 100
      const abs = Math.abs(diff)
      const text = `${diff > 0 ? '+' : ''}${abs > 0 && abs < 1 ? diff.toFixed(1) : Math.round(diff)}%`
      if (diff > 0) return { cls: 'up', icon: 'trending_up', text }
      if (diff < 0) return { cls: 'down', icon: 'trending_down', text }
      return { cls: 'flat', icon: 'trending_flat', text }
    }
  }
}
</script>

<style scoped>
/* Tuiles uniformes : même structure (en-tête, valeur, pied) et même hauteur par ligne */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 14px;
}
.kpi-tile {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 124px;
  background: #fff;
  border-radius: 5.6px;
  padding: 16px 18px;
  border: 1px solid #f1edf5;
  box-shadow: 0 2px 5.6px rgba(107, 31, 77, 0.08);
}
.kpi-head {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.kpi-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 5.6px;
  background: #f1edf5;
  color: #6b1f4d;
}
.kpi-icon .material-icons {
  font-size: 18px;
}
.kpi-label-wrap {
  min-width: 0;
}
.kpi-label {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  color: #555e75;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.25;
}
.kpi-help {
  font-size: 13px;
  color: #9ba3b7;
  margin-left: 3px;
  vertical-align: middle;
  cursor: help;
}
.ov-help-title,
.activity-subtitle {
  display: inline-flex;
  align-items: center;
}
.kpi-value {
  font-size: 26px;
  font-weight: 700;
  color: #121826;
  line-height: 1.1;
}
.kpi-foot {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 22px;
  margin-top: auto;
}
.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 20px;
}
.trend-badge .material-icons {
  font-size: 14px;
}
.trend-badge.up { background: #e6f9f0; color: #1a9e5e; }
.trend-badge.down { background: #fdf0f0; color: #d93025; }
.trend-badge.flat { background: #f1edf5; color: #6b1f4d; }
.kpi-prev {
  font-size: 11px;
  color: #9ba3b7;
}
.kpi-tile--locked {
  grid-column: span 3;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  font-size: 13px;
  color: #121826;
}
.kpi-tile--locked p {
  flex: 1;
}
@media (max-width: 700px) {
  .kpi-tile--locked { grid-column: auto; flex-direction: column; align-items: flex-start; }
}
.kpi-note {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 10px 2px 0;
  font-size: 11px;
  color: #9ba3b7;
}
.kpi-note .material-icons {
  font-size: 14px;
}

/* Ligne 2 */
.kpi-row2 {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
  gap: 20px;
  align-items: stretch;
  margin-top: 16px;
}
.kpi-row2--single {
  grid-template-columns: minmax(0, 1fr);
}
@media (max-width: 1100px) {
  .kpi-row2 { grid-template-columns: minmax(0, 1fr); }
}
.activity-subtitle {
  margin: 4px 0 8px;
  font-size: 11px;
  font-weight: 600;
  color: #555e75;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.activity-subtitle + .activity-empty,
.activity-list + .activity-subtitle,
.failure-bar + .activity-list {
  margin-top: 8px;
}
.activity-list + .activity-subtitle,
.activity-empty + .activity-subtitle {
  margin-top: 18px;
}
.activity-empty {
  font-size: 12px;
  color: #9ba3b7;
}
.activity-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.activity-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #121826;
}
.activity-label {
  flex-shrink: 0;
  min-width: 80px;
}
.activity-list--failures .activity-label {
  flex: 1;
  min-width: 0;
}
.activity-list strong small {
  font-weight: 400;
  color: #9ba3b7;
}
.activity-bar {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #f1edf5;
  overflow: hidden;
}
.activity-bar span {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: #6b1f4d;
}
.failure-bar {
  display: flex;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  background: #f1edf5;
}
.failure-segment-wrap {
  display: block;
  height: 100%;
}
.failure-segment {
  display: block;
  width: 100%;
  height: 100%;
}
.failure-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
/* Couleurs du $status-styles de Posts2 : failed, awaiting_approval, pending */
.failure-segment--failed { background: #ff6969; }
.failure-segment--not_published { background: #ff8820; }
.failure-segment--not_validated { background: #ab79d5; }
.kpi-skeleton {
  min-height: 124px;
  border-radius: 5.6px;
  background: linear-gradient(90deg, #f1edf5 25%, #f8f5fa 50%, #f1edf5 75%);
  background-size: 200% 100%;
  animation: ov-shimmer 1.4s infinite;
}
.kpi-skeleton--chart {
  min-height: 300px;
}
/* Le canvas a une largeur intrinsèque de 1000 px : sans ces bornes, il élargit la carte (scroll horizontal) */
.kpi-chart {
  min-width: 0;
}
.kpi-chart-body {
  position: relative;
  min-width: 0;
  overflow: hidden;
}
.kpi-chart-body >>> canvas {
  max-width: 100%;
}
.overview-kpis--fill {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.overview-kpis--fill .kpi-row2 {
  flex: 1;
  min-height: 0;
  grid-template-rows: minmax(0, 1fr);
}
.overview-kpis--fill .kpi-grid + .kpi-row2,
.overview-kpis--fill > .kpi-row2:first-child {
  margin-top: 0;
}
.overview-kpis--fill .kpi-chart {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.overview-kpis--fill .kpi-chart-body {
  flex: 1;
  min-height: 120px;
}
.overview-kpis--fill .kpi-skeleton--chart {
  min-height: 0;
  height: 100%;
}
@keyframes ov-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
</style>
